import {
  faFileAudio,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { parse, stringify } from "csv/browser/esm/sync";
import JSZip from "jszip";
import React from "react";
import shortid from "shortid";
import { SelectList } from "web-ui";
import { TextArea, TextInput } from "web-ui/juicy";
import { LegacyButton as Button, LegacyToggle as Toggle } from "web-ui/legacy";

import {
  finalStatuses,
  getDuolingoTtsLanguage,
  getDuolingoTtsMp3,
  getTtsHash,
} from "../../../api/duolingoTts";
import styles from "../../../styles/AudioGenerator/BulkEntryScreen.module.scss";
import { downloadData } from "../../../util/download";
import { HelpButton } from "../../HelpButton";
import type { TargetService } from "../AudioGenerator";

import type { CommonEntryScreenProps, RowData } from "./EntryScreen";
import {
  displayNamesMapping,
  doDuolingoTtsRequest,
  doTtsGenerationRequest,
  duolingoTtsFields,
  duolingoTtsSpeeds,
  persistDuolingoTtsRows,
  ttsGenerationFields,
  universalFields,
} from "./EntryScreen";
/* eslint-disable react/jsx-no-literals */

const CsvUploader = ({
  targetService,
  populateData,
}: {
  targetService: TargetService;
  populateData: (data: Record<string, RowData>[]) => void;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const expectedHeaders = universalFields.concat(
    targetService === "duolingo-tts" ? duolingoTtsFields : ttsGenerationFields,
  );

  const expectedHeadersText = `${expectedHeaders
    .slice(0, -1)
    .join(", ")}, and ${expectedHeaders.at(-1)}`;
  const instructions = `You can upload a CSV file with data you are requesting, or enter it manually. The CSV must contain a header row with columns labeled ${expectedHeadersText} (in any order).`;

  return (
    <>
      <input
        accept="text/csv"
        className={styles["csv-uploader"]}
        onChange={event => {
          const file = event.target.files?.[0];
          if (file) {
            const fileReader = new FileReader();
            fileReader.readAsText(file);
            fileReader.onload = () => {
              if (fileReader.result && typeof fileReader.result === "string") {
                const csvString = fileReader.result;
                const rows: object[] = parse(csvString, {
                  cast: (value, context) => {
                    if (context.header) {
                      return value;
                    }
                    if (value === "") {
                      return undefined;
                    }
                    if (
                      context.column === "breakTime" ||
                      context.column === "prosody"
                    ) {
                      return +value;
                    }
                    if (context.column === "isRawSsml") {
                      return value.toUpperCase() === "TRUE";
                    }
                    return value;
                  },
                  columns: true,
                });
                if (
                  rows.length > 0 &&
                  expectedHeaders.every(header =>
                    Object.keys(rows[0]).includes(header),
                  )
                ) {
                  populateData(
                    rows.map(row => ({
                      ...row,
                      key: shortid.generate(),
                      status: "not_submitted",
                    })),
                  );
                } else {
                  alert(
                    `An invalid CSV file was uploaded. Please ensure you have uploaded a CSV file with headers ${expectedHeadersText} and at least one non-header row.`,
                  );
                  return;
                }
              }
            };
          }
        }}
        ref={inputRef}
        type="file"
      />{" "}
      <div className={styles["csv-uploader-interface"]}>
        <HelpButton instructions={instructions} />
        <Button onClick={() => inputRef.current?.click()} variant="solid">
          Upload a CSV
        </Button>
      </div>
    </>
  );
};

const getDefaultEntry = () => ({
  key: shortid.generate(),
  speed: "normal",
  status: "not_submitted",
});

export const BulkEntryScreen = ({
  isSubmitted,
  lastUpdated,
  setIsSubmitted,
  setLastUpdated,
  setShouldPollForStatus,
  shouldPollForStatus,
  targetService,
  voices,
}: CommonEntryScreenProps) => {
  const [generationData, setGenerationData] = React.useState<
    Record<string, RowData>[]
  >([
    // Having one entry in the array makes it more clear to users
    // how to begin using the page.
    getDefaultEntry(),
  ]);
  const updateOneGenerationDataRow = (
    index: number,
    replacementDataRow: Record<string, RowData>,
  ) =>
    setGenerationData(currentData => [
      ...currentData.slice(0, index),
      replacementDataRow,
      ...currentData.slice(index + 1),
    ]);

  const isGenerated = generationData.every(
    row => typeof row.status === "string" && finalStatuses.has(row.status),
  );

  const [ttsGenerationMp3Data, setTtsGenerationMp3Data] = React.useState<
    Record<string, Blob>
  >({});

  const submitTtsRequest = React.useCallback(
    async (isFirstCall = false) => {
      const doRequest =
        targetService === "duolingo-tts"
          ? doDuolingoTtsRequest
          : doTtsGenerationRequest;

      const statuses = await Promise.all(
        generationData.map(async (row, index) => {
          if (typeof row.status === "string" && finalStatuses.has(row.status)) {
            return row.status;
          }
          const key = row.key;
          if (typeof key !== "string") {
            return "error";
          }
          const functionOptions =
            targetService === "duolingo-tts"
              ? { shouldSubmitNewRequest: isFirstCall }
              : {
                  onGenerated: (mp3Data: Blob) =>
                    setTtsGenerationMp3Data(currentMp3Data => ({
                      ...currentMp3Data,
                      [key]: mp3Data,
                    })),
                };

          const status = await doRequest(row, functionOptions);
          setLastUpdated(new Date());
          updateOneGenerationDataRow(index, { ...row, status });
          return status;
        }),
      );
      const areAllDone = statuses.every(status => finalStatuses.has(status));
      if (areAllDone) {
        setShouldPollForStatus(false);
        // We have to do this as a batch update after all are done rather than in the loop as an
        // onGenerated handler because we could get a race condition where two pieces of speech
        // get the stored version before the other one writes and they overwrite each other (since it's not a
        // React hook, it won't take care of this for us like the setTtsGenerationMp3Data call does).
        if (targetService === "duolingo-tts") {
          persistDuolingoTtsRows(
            generationData.map((row, index) => ({
              ...row,
              date: lastUpdated.toJSON(),
              // Because the updateOneGenerationDataRow function calls a React setState function,
              // and those are batched, we can't guarantee that the state will be updated
              // before this persistence function is called, so we access the most up-to-date
              // status directly from the status array.
              status: statuses[index],
            })),
          );
        }
      }
    },
    [
      generationData,
      lastUpdated,
      setLastUpdated,
      setShouldPollForStatus,
      targetService,
    ],
  );

  React.useEffect(() => {
    if (shouldPollForStatus) {
      const interval = setInterval(() => {
        submitTtsRequest();
      }, 15 * 1000);

      return () => {
        clearInterval(interval);
      };
    }

    return () => 0;
  }, [shouldPollForStatus, submitTtsRequest]);

  const areAllSpecifiedVoicesValid = generationData.every(
    entry =>
      typeof entry.voice === "undefined" ||
      (typeof entry.voice === "string" && voices.includes(entry.voice)),
  );

  const hasRequiredFields =
    generationData.length > 0 &&
    generationData.every(
      entry =>
        typeof entry.text === "string" &&
        typeof entry.voice === "string" &&
        (targetService === "tts-generation-backend" ||
          typeof entry.speed === "string"),
    );

  const permanentCols = universalFields.length + 1; // +1 for the column for status
  const beforeGenerationCols = 1; // column for delete row button
  const targetSpecificCols =
    targetService === "duolingo-tts"
      ? duolingoTtsFields.length
      : ttsGenerationFields.length;

  const totalCols =
    permanentCols +
    targetSpecificCols +
    (isSubmitted ? 0 : beforeGenerationCols);

  const getMp3DataForRow = async (row: Record<string, RowData>) => {
    if (targetService === "tts-generation-backend") {
      if (typeof row.key !== "string") {
        throw new Error("No unique row identifier");
      }
      const storedData = ttsGenerationMp3Data[row.key];
      if (!storedData) {
        throw new Error("No MP3 data found");
      }
      return { id: row.key, mp3: storedData };
    }
    if (
      typeof row.voice !== "string" ||
      typeof row.text !== "string" ||
      typeof row.speed !== "string" ||
      !(typeof row.ssml === "undefined" || typeof row.ssml === "string")
    ) {
      throw new Error("An error occurred in download link generation.");
    }
    const language = await getDuolingoTtsLanguage(row.voice);
    const id = getTtsHash(language, row.voice, row.speed, row.text, row.ssml);
    const mp3 = await getDuolingoTtsMp3(row.voice, id);

    return { id, mp3 };
  };

  return (
    <>
      {isSubmitted ? null : (
        <CsvUploader
          populateData={setGenerationData}
          targetService={targetService}
        />
      )}
      <table className={styles["table"]}>
        <thead>
          <tr>
            <th>{displayNamesMapping.text}</th>
            <th>{displayNamesMapping.ssml}</th>
            <th>{displayNamesMapping.voice}</th>
            {(targetService === "duolingo-tts"
              ? duolingoTtsFields
              : ttsGenerationFields
            ).map(fieldName => (
              <th key={fieldName}>{displayNamesMapping[fieldName]}</th>
            ))}
            <th>Status</th>
            {isSubmitted ? null : <th>Delete</th>}
          </tr>
        </thead>
        <tbody>
          {generationData.map((generationDataRow, rowNum) => (
            <tr key={generationDataRow.key?.toString()}>
              <td>
                <TextArea
                  cols={50}
                  onChange={e => {
                    updateOneGenerationDataRow(rowNum, {
                      ...generationDataRow,
                      text: e.currentTarget.value || undefined,
                    });
                  }}
                  placeholder="Text to synthesize"
                  rows={3}
                  state={isSubmitted ? "disabled" : "enabled"}
                  value={generationDataRow.text?.toString()}
                />
              </td>
              <td>
                <TextArea
                  cols={50}
                  onChange={e => {
                    updateOneGenerationDataRow(rowNum, {
                      ...generationDataRow,
                      ssml: e.currentTarget.value || undefined,
                    });
                  }}
                  placeholder={
                    isSubmitted
                      ? undefined
                      : "Speech Synthesis Markup Language to fine-tune the synthesis. Leave blank for no SSML."
                  }
                  rows={3}
                  state={isSubmitted ? "disabled" : "enabled"}
                  value={generationDataRow.ssml?.toString()}
                />
              </td>
              <td>
                <SelectList
                  disabled={isSubmitted}
                  items={voices.map(voice => ({
                    text: voice,
                  }))}
                  onChange={e =>
                    updateOneGenerationDataRow(rowNum, {
                      ...generationDataRow,
                      voice: voices[e.selectedIndices[0]],
                    })
                  }
                  popoverPosition={{
                    direction: "down",
                    manualPositioning: true,
                    style: {
                      marginTop: 50,
                      maxHeight: 300,
                      position: "absolute",
                    },
                    zIndex: 100,
                  }}
                  selectedIndices={
                    typeof generationDataRow.voice === "string"
                      ? [voices.indexOf(generationDataRow.voice)]
                      : []
                  }
                  showSearch={true}
                  text={generationDataRow.voice ?? "Voice name"}
                />
              </td>
              {targetService === "duolingo-tts" ? (
                <td>
                  <SelectList
                    disabled={isSubmitted}
                    items={duolingoTtsSpeeds.map(speed => ({ text: speed }))}
                    onChange={e =>
                      updateOneGenerationDataRow(rowNum, {
                        ...generationDataRow,
                        speed: duolingoTtsSpeeds[e.selectedIndices[0]],
                      })
                    }
                    popoverPosition={{
                      direction: "down",
                      manualPositioning: true,
                      style: {
                        marginTop: 50,
                        maxHeight: 300,
                        position: "absolute",
                      },
                      zIndex: 100,
                    }}
                    selectedIndices={
                      typeof generationDataRow.speed === "string"
                        ? [duolingoTtsSpeeds.indexOf(generationDataRow.speed)]
                        : []
                    }
                    text={generationDataRow.speed ?? "Speed"}
                  />
                </td>
              ) : (
                <>
                  <td>
                    <Toggle
                      checked={
                        typeof generationDataRow.isRawSsml === "boolean" &&
                        generationDataRow.isRawSsml
                      }
                      disabled={isSubmitted}
                      onChange={e =>
                        updateOneGenerationDataRow(rowNum, {
                          ...generationDataRow,
                          isRawSsml: e.target.checked,
                        })
                      }
                    />
                  </td>
                  <td>
                    <TextInput
                      inputMode="decimal"
                      min={0}
                      onChange={e =>
                        updateOneGenerationDataRow(rowNum, {
                          ...generationDataRow,
                          prosody: e.target.valueAsNumber || undefined,
                        })
                      }
                      placeholder="1"
                      state={isSubmitted ? "disabled" : "enabled"}
                      type="number"
                      value={generationDataRow.prosody?.toString() ?? ""}
                    />
                  </td>
                  <td>
                    <TextInput
                      inputMode="numeric"
                      min={0}
                      onChange={e =>
                        updateOneGenerationDataRow(rowNum, {
                          ...generationDataRow,
                          breakTime: e.target.valueAsNumber || undefined,
                        })
                      }
                      placeholder="0"
                      state={isSubmitted ? "disabled" : "enabled"}
                      type="number"
                      value={generationDataRow.breakTime?.toString() ?? ""}
                    />
                  </td>
                </>
              )}
              <td>
                {generationDataRow.status === "created" ? (
                  <Button
                    onClick={async () => {
                      const { id, mp3 } =
                        await getMp3DataForRow(generationDataRow);
                      downloadData(mp3, `${id}.mp3`);
                    }}
                    variant="solid"
                  >
                    <FontAwesomeIcon icon={faFileAudio} />
                  </Button>
                ) : (
                  // Replace snake case with non-breaking spaces
                  generationDataRow.status?.toString()?.replace("_", "\xa0")
                )}
              </td>
              {isSubmitted ? null : (
                <td>
                  <Button
                    onClick={() =>
                      setGenerationData([
                        ...generationData.slice(0, rowNum),
                        ...generationData.slice(rowNum + 1),
                      ])
                    }
                    variant="text"
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </Button>
                </td>
              )}
            </tr>
          ))}
          {isSubmitted ? null : (
            <tr className={styles["newRow"]}>
              <td
                colSpan={totalCols}
                onClick={() => {
                  setGenerationData([...generationData, getDefaultEntry()]);
                }}
              >
                <div>
                  <FontAwesomeIcon icon={faPlus} /> New Row
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {isSubmitted ? (
        <>
          {isGenerated ? (
            <Button
              onClick={async () => {
                const generatedRows = generationData.filter(
                  row => row.status === "created",
                );
                const audioData = await Promise.all(
                  generatedRows.map(row => getMp3DataForRow(row)),
                );
                const zipFile = new JSZip();
                audioData.forEach(({ id, mp3 }) =>
                  zipFile.file(`${id}.mp3`, mp3),
                );
                const manifestData = generatedRows.map((row, index) => ({
                  ...Object.fromEntries(
                    Object.entries(row).filter(
                      ([key]) => !(key === "status" || key === "key"),
                    ),
                  ),
                  fileName: `${audioData[index].id}.mp3`,
                }));
                const manifest = stringify(manifestData, { header: true });
                zipFile.file("manifest.csv", manifest);
                const archive = await zipFile.generateAsync({ type: "blob" });
                downloadData(archive, "generated-audio.zip");
              }}
              variant="solid"
            >
              Download all
            </Button>
          ) : (
            <p>
              Generation is in progress! The status was last updated at{" "}
              {lastUpdated.toTimeString()}. As your speech files generate, you
              will be able to download them one at a time, or once all are
              complete, you can download them all in a single ZIP file. Please
              be patient when generating audio with World character voices as we
              may need to activate some cloud resources before generating the
              audio, which could take up to 5 minutes.
            </p>
          )}
          <Button
            onClick={() => {
              setGenerationData(
                generationData.map(row => ({
                  ...row,
                  status: "not_submitted",
                })),
              );
              setTtsGenerationMp3Data({});
              setShouldPollForStatus(false);
              setIsSubmitted(false);
            }}
            variant="stroke"
          >
            {isGenerated ? "Generate different audio" : "Edit submission"}
          </Button>
        </>
      ) : (
        <>
          {areAllSpecifiedVoicesValid ? null : (
            <p>
              One or more voices listed above is not a valid voice. Please
              select a voice from the dropdown list.
            </p>
          )}
          <Button
            disabled={!(hasRequiredFields && areAllSpecifiedVoicesValid)}
            onClick={() => {
              setIsSubmitted(true);
              setShouldPollForStatus(true);
              submitTtsRequest(true);
            }}
            variant="solid"
          >
            Submit
          </Button>
        </>
      )}
    </>
  );
};
